/**
 * Seller trust badge - visualises the 0-100 trust score computed by the
 * backend trust engine (backend/src/services/trust.ts). Tiers mirror the
 * server's `trustTier()` so the badge can be derived from either field.
 */

export type TrustTier = 'GOLD' | 'TRUSTED' | 'STANDARD' | 'WATCH';

export function tierFromScore(score: number): TrustTier {
  if (score >= 80) return 'GOLD';
  if (score >= 65) return 'TRUSTED';
  if (score >= 40) return 'STANDARD';
  return 'WATCH';
}

const TIER_CONFIG: Record<TrustTier, { label: string; blurb: string; cls: string; dot: string }> = {
  GOLD:     { label: 'Gold seller',    blurb: 'Long clean record - payouts in 24h, zero upheld disputes recently.', cls: 'bg-gold text-ink',                          dot: 'bg-ink' },
  TRUSTED:  { label: 'Trusted seller', blurb: 'Strong track record of clean, confirmed sales.',                     cls: 'bg-sage text-cream',                        dot: 'bg-cream' },
  STANDARD: { label: 'Souqli seller',  blurb: 'Every sale is payment-protected with a dispute window.',              cls: 'bg-cream-deep text-ink border border-warmline', dot: 'bg-gold-dark' },
  WATCH:    { label: 'Extended protection', blurb: 'New or flagged account - buyers get an extended 96h inspection window and manual payout review.', cls: 'bg-clay text-cream', dot: 'bg-cream' },
};

interface Props {
  score?: number;
  tier?: TrustTier;
  showScore?: boolean;
  className?: string;
}

export function TrustBadge({ score, tier, showScore = true, className = '' }: Props) {
  const t = tier ?? (typeof score === 'number' ? tierFromScore(score) : 'STANDARD');
  const cfg = TIER_CONFIG[t];
  return (
    <span
      className={`pill inline-flex items-center gap-1.5 ${cfg.cls} ${className}`}
      title={cfg.blurb}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
      {showScore && typeof score === 'number' && (
        <span className="mono text-[9px] opacity-80">{score}</span>
      )}
    </span>
  );
}

/** Compact trust meter for dashboards - score bar in the V2 language. */
export function TrustMeter({ score, className = '' }: { score: number; className?: string }) {
  const t = tierFromScore(score);
  const barColour = t === 'GOLD' ? 'bg-gold' : t === 'TRUSTED' ? 'bg-sage' : t === 'STANDARD' ? 'bg-gold-dark' : 'bg-clay';
  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="stamp text-gold-dark text-[9px]">TRUST SCORE</span>
        <span className="mono text-[11px] text-ink">{score} / 100</span>
      </div>
      <div className="h-1.5 rounded-full bg-warmline overflow-hidden">
        <div className={`h-full rounded-full ${barColour} transition-all`} style={{ width: `${Math.max(4, Math.min(100, score))}%` }} />
      </div>
      <div className="mt-1.5"><TrustBadge score={score} showScore={false} /></div>
    </div>
  );
}
